/**
 * MessageAuraPill - Give aura to a message (goes to the sender).
 * Only shown on messages you did not send. Tapping the orb opens the shared
 * `AuraCategoriesModal` so the giving UX matches every other surface
 * (post feed, composer, comments).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { decrementRemaining } from '../../store/aura.reducer';
import { createAura } from '../../helpers/auraHelpers';
import AuraCategoriesModal from '../Aura/AuraCategoriesModal';

function MessageAuraPill({ messageId, messageType, senderId, auraCount = 0, onAuraGiven }) {
  const dispatch = useDispatch();
  const [count, setCount] = useState(auraCount);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const pulseTimeoutRef = useRef(null);

  useEffect(() => {
    setCount(auraCount);
  }, [auraCount]);

  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    };
  }, []);

  const closeModal = () => setShowModal(false);

  const handleRadiate = async ({ entries, totalSelected }) => {
    if (loading) return;
    setLoading(true);
    try {
      for (const { categoryId, points } of entries) {
        await createAura({
          targetId: senderId,
          targetType: 'user',
          messageId,
          messageType,
          categoryId,
          points,
        });
      }
      toast.success(totalSelected === 1 ? 'Aura sent!' : 'Aura radiated!');
      closeModal();
      setCount((c) => c + totalSelected);
      dispatch(decrementRemaining(totalSelected));
      onAuraGiven?.(totalSelected);
      setPulsing(true);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => {
        setPulsing(false);
        pulseTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="relative inline-flex ml-1.5 align-middle">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className={`inline-flex items-center justify-center gap-0.5 w-6 h-6 rounded-full text-xs bg-aura/25 text-aura hover:bg-aura/40 border border-aura/50 transition-colors ${pulsing ? 'animate-pulseSoft' : ''}`}
        title="Give aura"
      >
        <span>✦</span>
        {count > 0 && <span className="font-medium">{count}</span>}
      </button>

      <AuraCategoriesModal
        open={showModal}
        onClose={closeModal}
        onRadiate={handleRadiate}
        loading={loading}
      />
    </span>
  );
}

export default MessageAuraPill;
